// Homepage.tsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import MobileCalendar from '../components/MobileCalendar';
import MapComponent from '../components/MapComponent';
import { GoogleMapsContext } from '../App';
import KindOfTrip from '../components/homepage/KindOfTrip';
import WhyUs from '../components/homepage/WhyUs';
import CountryMap from '../components/homepage/CountryMap';
import LoginSection from '../components/homepage/LoginSection';
import { City } from '../types/trip';
import CountrySearch from '../components/CountrySearch';
import { searchFlights } from '../services/flightService';
import { geminiService } from '../services/Gemini';
import { tripApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import CustomDateInput, { CustomTextArea } from '../components/homepage/CustomDateInput';
// Import custom components and styles

interface CityColor {
  city: string;
  color: string;
}

interface Country {
  name: string;
  country: string;
}

// Update the tripType type definition
type TripType = 'manual' | 'ai-assist' | 'feeling-lucky';

interface KindOfTripProps {
  tripType: TripType;
  onTripTypeChange: (type: TripType) => void;
  options: Array<{ value: TripType; label: string }>;
}

// Main functional component
const Homepage: React.FC = () => {
  const { user, loading: authLoading, initialized } = useAuth();
  const navigate = useNavigate();
  const isGoogleMapsLoaded = useContext(GoogleMapsContext);
  const [tripType, setTripType] = useState<TripType>('manual');
  // Stores the type of trip selected (manual, AI assistant, or feeling lucky)

  const [description, setDescription] = useState('');
  // Stores the user's description for AI assistant

  const [selectedCountry, setSelectedCountry] = useState<City | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<City[]>([]);
  // Stores the countries selected by the user

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  // Stores the selected date range

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  // Stores the start and end dates separately

  const [isOpen, setIsOpen] = useState(false);
  // Controls the visibility of the date picker

  const [activeInput, setActiveInput] = useState<'start' | 'end' | null>(null);
  const [isFlexible, setIsFlexible] = useState(false);
  // Determines if the date selection is flexible

  const [cityColors, setCityColors] = useState<CityColor[]>([]);
  // Stores colors associated with cities (used in MapComponent)

  const [searchOptions, setSearchOptions] = useState({
    location: false,
    flights: false,
    accommodation: false,
    transport: false,  // Don't enable transport search by default
  });
  // Stores which search options are included

  const [showTextBox, setShowTextBox] = useState(false);
  // Controls visibility of the filter text box in "Feeling Lucky" option

  const [filterText, setFilterText] = useState('');
  // Stores the text entered in the filter text box

  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  // Stores the currently active filter type

  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  // Stores selected months for flexible date selection

  const calendarRef = useRef<HTMLDivElement>(null);
  // Ref for the calendar container to detect clicks outside

  // Add loading state
  const [isSearching, setIsSearching] = useState(false);

  // Add this near the other useState hooks
  const [descriptionFocused, setDescriptionFocused] = useState(false);
  
  // Track if we've waited long enough to show an error
  const [mapLoadTimeout, setMapLoadTimeout] = useState(false);
  // Simple mobile detection - screens smaller than 768px are considered mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for responsive calendar
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add at the top with other useState hooks
  const [countryInput, setCountryInput] = useState('');
  const [countryFocused, setCountryFocused] = useState(false);

  // Update the click handlers for both inputs
  const handleStartDateClick = () => {
    setActiveInput('start');
    setIsOpen(true);
  };

  const handleEndDateClick = () => {
    setActiveInput('end');
    setIsOpen(true);
  };

  // Add this effect to log the state changes
  useEffect(() => {
    console.log("isFlexible state:", isFlexible);
  }, [isFlexible]);

  // Clear any lingering session storage on component mount to ensure clean state
  useEffect(() => {
    // Clear any existing form state to prevent auto-selection from previous sessions
    sessionStorage.removeItem('pendingTripFormState');
  }, []);

  // Set a timeout for map loading to show error message if it takes too long
  useEffect(() => {
    if (!isGoogleMapsLoaded) {
      const timeout = setTimeout(() => {
        setMapLoadTimeout(true);
      }, 15000); // 15 seconds

      return () => clearTimeout(timeout);
    } else {
      setMapLoadTimeout(false);
    }
  }, [isGoogleMapsLoaded]);

  // Toggle flexible dates mode
  const toggleFlexible = (value: boolean) => {
    console.log("Setting flexible mode to:", value); // Debug log before setting
    setIsFlexible(value);
    console.log("Flexible mode set to:", value); // Debug log after setting
    
    // Make sure we have dates set if they're missing
    if (!startDate) {
      const today = new Date();
      setStartDate(today);
    }
    
    if (!endDate && startDate) {
      // Set end date to 7 days after start date if not set
      const newEndDate = new Date(startDate);
      newEndDate.setDate(newEndDate.getDate() + 7);
      setEndDate(newEndDate);
    }
  };

  // Add this to your existing useEffect for handling clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add this effect after the useEffect for handleClickOutside
  useEffect(() => {
    // Helper function to handle Google Maps autocomplete input fields
    const setupGooglePlacesInputs = () => {
      // Initial setup of any existing inputs
      const setupExistingInputs = () => {
        const pacInputs = document.querySelectorAll('.pac-target-input');
        pacInputs.forEach(input => {
          if (input instanceof HTMLInputElement) {
            // Create Tailwind classes based on input state
            const updateInputStyling = (isActive: boolean) => {
              // Just style the input without creating labels
              // The CountrySearch component handles its own placeholder
                input.classList.add('w-full', 'border', 'border-gray-300', 'rounded-lg', 'bg-white', 'h-12', 'px-4', 'py-3', 'font-buenos-aires', 'text-base', 'transition-all', 'duration-200');
            };
            
            // Check on focus
            input.addEventListener('focus', () => updateInputStyling(true));
            
            // Check on blur
            input.addEventListener('blur', () => updateInputStyling(false));
            
            // Check on input
            input.addEventListener('input', () => {
              updateInputStyling(document.activeElement === input);
            });
            
            // Initial check
            updateInputStyling(document.activeElement === input);
          }
        });
      };
      
      // Setup existing inputs
      setupExistingInputs();
      
      // Watch for new inputs being added to the DOM
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if any added nodes contain Google Places inputs
            let shouldSetup = false;
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (element.classList && element.classList.contains('pac-target-input')) {
                  shouldSetup = true;
                } else if (element.querySelectorAll) {
                  const inputs = element.querySelectorAll('.pac-target-input');
                  if (inputs.length > 0) {
                    shouldSetup = true;
                  }
                }
              }
            });
            
            if (shouldSetup) {
              // Use setTimeout to ensure the DOM is fully updated
              setTimeout(setupExistingInputs, 10);
            }
          }
        });
      });
      
      // Start observing the document body
      observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });
      
      // Return cleanup function
      return () => {
        observer.disconnect();
      };
    };
    
    // Run setup when the tab changes to 'manual'
    let cleanup: (() => void) | undefined;
    if (tripType === 'manual') {
      cleanup = setupGooglePlacesInputs();
    }
    
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [tripType]);

  // Handler for changing the trip type (manual, AI assistant, feeling lucky)
  const handleTripTypeChange = (value: string) => {
    setTripType(value as TripType);
  };

  const getNext12Months = () => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      months.push({
        month: date.toLocaleString('default', { month: 'long' }),
        year: date.getFullYear().toString()
      });
    }
    return months;
  };

  // Handler for date range change from the date picker
  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setDateRange(dates);
    if (end) {
      setIsOpen(false);
    }
  };

  // Handler to toggle search options (location, flights, accommodation, transport)
  const handleSearchOptionToggle = (option: keyof typeof searchOptions) => {
    setSearchOptions((prevOptions) => ({
      ...prevOptions,
      [option]: !prevOptions[option],
    }));
  };

  // Handler for filter button click in "Feeling Lucky" option
  const handleFilterButtonClick = () => {
    setShowTextBox(true);
  };

  // Handler for clicking on a filter type (region, climate, budget, other)
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Add debug logs
    console.log('Form submission values:', {
      tripType,
      selectedCountries,
      selectedCountriesLength: selectedCountries.length,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      selectedCountry: selectedCountry
    });
    
    // Different validation rules based on trip type
    if (tripType === 'manual') {
      // For manual trips, require at least one country, start date, and end date
      if (selectedCountries.length === 0 || !startDate || !endDate) {
        alert('Please enter all trip details (At least one country, Start Date, End Date)');
        return;
      }
    } else if (tripType === 'ai-assist') {
      // For AI assist, only require description and dates
      if (!description || !startDate || !endDate) {
        alert('Please provide a trip description and select dates');
        return;
      }
    } else {
      // For any other trip type (feeling lucky)
      if (!startDate || !endDate) {
        alert('Please select start and end dates');
        return;
      }
    }

    // Create form data object AFTER validation
    const formData = {
      tripType,
      description,
      selectedCountries: tripType === 'manual' ? selectedCountries : selectedCountry ? [selectedCountry] : [],
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      searchOptions
    };

    // Check authentication AFTER validation - only prompt login if form is complete
    if (!checkAuthAndProceed('planning', formData)) {
      return;
    }

    if (tripType === 'manual') {
      // For manual trips, we've validated selectedCountries is not empty
      navigate('/trip-overview', {
        state: {
          tripDetails: {
            tripType: 'manual',
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            // Use the countries names for title/overview purposes
            selectedCountries: selectedCountries.map(country => country.country),
            // Set a trip title based on the countries selected
            tripTitle: selectedCountries.length === 1 
              ? `Trip to ${selectedCountries[0].name}`
              : `Trip to ${selectedCountries.map(c => c.name).join(', ')}`,
            description: ''
          },
          // Don't pass countries as cities - instead let the user add cities in the trip overview
          selectedCities: [], // Empty array instead of selectedCountries
          daysArray: [], // Empty since no cities are pre-selected
          cityCosts: {},
          cityColors: [], // Empty since no cities are pre-selected
          cityDayDetails: {},
          totalTripCost: 0,
          includeFlightSearch: searchOptions.flights,
          includeAccommodationSearch: searchOptions.accommodation,
          // Add a flag to indicate this is a manual trip where cities need to be added
          isNewManualTrip: true,
          // Keep track of the countries for reference
          selectedCountriesInfo: selectedCountries
        }
      });
      return;
    }
  
    if (tripType === 'ai-assist') {
      if (!description) {
        alert('Please provide a trip description');
        return;
      }
      navigate('/ai-assist', {
        state: {
          tripDetails: {
            description: description,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            selectedCities: selectedCountry ? [selectedCountry] : [],
            selectedCountries: selectedCountry ? [selectedCountry.country] : [],
          },
          searchOptions: searchOptions,
        },
      });
      return;
    }
    
    // Feeling lucky trips are now handled by the handleSurpriseMe function
  };

  useEffect(() => {
    const savedFormState = sessionStorage.getItem('pendingTripFormState');
    if (savedFormState && user) {
      const formState = JSON.parse(savedFormState);
      
      // Restore the form state but NOT search options to prevent auto-selection
      setTripType(formState.tripType);
      setDescription(formState.description);
      setSelectedCountries(formState.selectedCountries);
      setStartDate(formState.startDate ? new Date(formState.startDate) : null);
      setEndDate(formState.endDate ? new Date(formState.endDate) : null);
      // Don't restore searchOptions - keep them as default (all false)
      
      // Clear the saved state
      sessionStorage.removeItem('pendingTripFormState');

      // Automatically continue with the trip planning
      if (formState.tripType === 'ai-assist') {
        navigate('/ai-assist', {
          state: {
            tripDetails: {
              description: formState.description,
              startDate: formState.startDate,
              endDate: formState.endDate,
              selectedCities: formState.selectedCountries.map((country: City) => country),
              selectedCountries: formState.selectedCountries.map((country: City) => country.country),
            },
            searchOptions: searchOptions, // Use current default searchOptions instead of restored ones
          },
        });
      } else {
        // For manual trip type, proceed with flight search using current searchOptions
        handleTripSubmission({ ...formState, searchOptions: searchOptions });
      }
    }
  }, [user, searchOptions]);

  const handleTripSubmission = async (formData: any) => {
    if (!formData.selectedCountries || !formData.startDate || !formData.endDate) {
      return;
    }

    setIsSearching(true);

    try {
      let flightData = null;
      
      // Only search for flights if the user has selected the flights option
      if (formData.searchOptions.flights) {
        const fromAirport = 'LHR';
        const toAirport = await geminiService.getAirportCode(formData.selectedCountries[0].name);

        flightData = await searchFlights({
          fromCity: fromAirport,
          toCity: toAirport,
          departureDate: formData.startDate,
          returnDate: formData.endDate
        });
      }

      const navigationState = {
        tripDetails: {
          tripType: formData.tripType,
          startDate: formData.startDate.split('T')[0],
          endDate: formData.endDate.split('T')[0],
          selectedCities: formData.selectedCountries,
          selectedCountries: formData.selectedCountries.map((country: City) => country.country),
        },
        selectedCities: formData.selectedCountries,
        outboundFlight: flightData?.outbound || null,
        inboundFlight: flightData?.inbound || null,
        includeFlightSearch: formData.searchOptions.flights,
        includeAccommodationSearch: formData.searchOptions.accommodation,
        allFlights: flightData?.allFlights || [],
        cityColors: formData.selectedCountries.map((country: City) => ({ 
          city: country.name, 
          color: getRandomColor(formData.selectedCountries.indexOf(country)) 
        })),
        daysArray: formData.selectedCountries.map(() => 1),
        cityCosts: {},
        cityDayDetails: {},
        totalTripCost: flightData?.outbound?.price || 0,
        preloadedFlightData: flightData
      };

      // Prefetch accommodation data if the option is enabled
      if (navigationState.includeAccommodationSearch) {
        console.log('Accommodation search enabled - will be handled by AI Assist or TripOverview');
      }

      navigate('/trip-overview', { state: navigationState });
    } catch (error) {
      navigate('/trip-overview', { 
        state: { 
          tripDetails: {
            tripType: formData.tripType,
            startDate: formData.startDate.split('T')[0],
            endDate: formData.endDate.split('T')[0],
            selectedCities: formData.selectedCountries,
            selectedCountries: formData.selectedCountries.map((country: City) => country.country),
          },
          includeFlightSearch: formData.searchOptions.flights,
          includeAccommodationSearch: formData.searchOptions.accommodation
        } 
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Handler for quick date selections (e.g., ±1 day)
  const handleQuickSelect = (days: number | null) => {
    if (days === null) {
      // Just keep the calendar open with existing dates for exact date selection
      return;
    }

    // Get today's date if no start date is selected
    const startBase = startDate || new Date();
    const endBase = startDate ? new Date(startDate) : new Date();
    
    // Add the specified days to calculate end date
    endBase.setDate(endBase.getDate() + days);
    
    // Update the date state
    setStartDate(startBase);
    setEndDate(endBase);
    
    // Close the calendar after selection
    setTimeout(() => {
    setIsOpen(false);
    }, 300);
  };

  // Map for converting month names to numbers
  const monthMap: { [key: string]: string } = {
    January: '01',
    February: '02',
    March: '03',
    April: '04',
    May: '05',
    June: '06',
    July: '07',
    August: '08',
    September: '09',
    October: '10',
    November: '11',
    December: '12',
  };

  // Handler for selecting a month in flexible date selection
  const handleMonthClick = (month: string, year: string) => {
    const monthNumber = monthMap[month];
    const start = new Date(`${year}-${monthNumber}-01`);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);

    setStartDate(start);
    setEndDate(end);

    setSelectedMonths((prev) =>
      prev.includes(`${month} ${year}`) ? prev.filter((m) => m !== `${month} ${year}`) : [...prev, `${month} ${year}`]
    );
  };

  const generateSurpriseTrip = async (filters: {
    region?: string;
    climate?: string;
    budget?: string;
    other?: string;
    startDate: string;
    endDate: string;
  }) => {
    try {
      return await tripApi.generateSurpriseTrip(filters);
    } catch (error) {
      throw error;
    }
  };
  
  // Add this state in the Homepage component
  const [isGeneratingSurprise, setIsGeneratingSurprise] = useState(false);
  
  // Add this function in the Homepage component
  const handleSurpriseMe = async () => {
    // Validate dates are selected
    if (!startDate || !endDate) {
      alert('Please select start and end dates for your surprise trip');
      return;
    }

    // Check authentication before proceeding
    if (!checkAuthAndProceed('surprise')) {
      return;
    }

    setIsGeneratingSurprise(true);
    
    try {
      // Only include filters that have actual text entered
      const filters: any = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };
      
      // Only add filter if user has entered text for the active filter
      if (activeFilter && filterText && filterText.trim()) {
        filters[activeFilter] = filterText.trim();
      }
      
      console.log('Generating surprise trip with filters:', filters);
      
      const surpriseTrip = await generateSurpriseTrip(filters);
      
      console.log('Surprise trip generated:', surpriseTrip);
      
      // Navigate to trip overview with the generated trip
      // Add a flag to identify this as a surprise trip
      navigate('/trip-overview', { 
        state: {
          ...surpriseTrip,
          isSurpriseTrip: true,
          fromAiAssist: true // Treat surprise trips similar to AI Assist trips
        }
      });
    } catch (error: any) {
      console.error('Error generating surprise trip:', error);
      
      // Show more detailed error message
      let errorMessage = 'Failed to generate surprise trip. Please try again.';
      
      if (error?.response?.data?.error) {
        errorMessage = `Error: ${error.response.data.error}`;
      } else if (error?.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setIsGeneratingSurprise(false);
    }
  };

  const handleFilterClick = (filterType: string) => {
    setActiveFilter(activeFilter === filterType ? null : filterType);
    setShowTextBox(true);
  };

  // Add this function to check if a date is in the past
  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Custom day className function to prevent highlighting outside month dates
  const getDayClassName = (date: Date) => {
    // Get the currently displayed month from the calendar
    const calendarDate = activeInput === 'start' ? (startDate || new Date()) : (endDate || new Date());
    const displayedMonth = calendarDate.getMonth();
    const displayedYear = calendarDate.getFullYear();
    
    const dateMonth = date.getMonth();
    const dateYear = date.getFullYear();
    
    // Check if this date is outside the currently displayed month
    const isOutsideDisplayedMonth = dateMonth !== displayedMonth || dateYear !== displayedYear;
    
    // If date is in range but outside the displayed month, don't highlight it
    if (startDate && endDate && isOutsideDisplayedMonth) {
      const isInRange = date >= startDate && date <= endDate;
      if (isInRange) {
        return 'react-datepicker__day--outside-month-range';
      }
    }
    
    return '';
  };

  // New function to handle adding a country to the selection
  const handleAddCountry = (country: City | null) => {
    if (!country) return;
    
    // Don't add if the country is already in the list
    if (selectedCountries.some(c => c.id === country.id)) return;
    
    // Add the country to the array and update the map
    setSelectedCountries(prev => [...prev, country]);
    
    // For backward compatibility with the rest of the code
    setSelectedCountry(country);
    
    // Update the map colors
    setCityColors(prev => [
      ...prev,
      { city: country.name, color: getRandomColor(selectedCountries.length) }
    ]);

    // Debug log
    console.log('Country added:', {
      country,
      selectedCountries: [...selectedCountries, country],
      selectedCountry: country
    });
  };

  // Function to generate a color for a new country
  const getRandomColor = (index: number) => {
    const colors = [
      '#3c869c', // Default blue
      '#e63946', // Red
      '#219ebc', // Light blue
      '#6a994e', // Green
      '#8338ec', // Purple
      '#ffb703', // Yellow
      '#f07167', // Coral
      '#6d597a', // Purple/gray
    ];
    return colors[index % colors.length];
  };

  // New function to remove a country from the selection
  const handleRemoveCountry = (countryId: string) => {
    setSelectedCountries(prev => prev.filter(c => c.id !== countryId));
    setCityColors(prev => prev.filter(c => 
      !selectedCountries.find(country => country.id === countryId || country.name === c.city)
    ));
    
    // If we removed the currently selected country, set it to the last one in the array
    if (selectedCountry?.id === countryId) {
      const remaining = selectedCountries.filter(c => c.id !== countryId);
      setSelectedCountry(remaining.length > 0 ? remaining[remaining.length - 1] : null);
    }
  };

  // Add helper function to check auth and redirect
  const checkAuthAndProceed = (action: 'planning' | 'surprise', formData?: any) => {
    if (!user) {
      // Save form state for later
      if (action === 'planning' && formData) {
        const stateToSave = {
          tripType,
          description,
          selectedCountries: formData.selectedCountries || selectedCountries,
          startDate: formData.startDate || startDate?.toISOString(),
          endDate: formData.endDate || endDate?.toISOString(),
          searchOptions
        };
        console.log('💾 Homepage: Saving form state before login redirect:', stateToSave);
        sessionStorage.setItem('pendingTripFormState', JSON.stringify(stateToSave));
      } else if (action === 'surprise') {
        sessionStorage.setItem('pendingTripFormState', JSON.stringify({
          tripType: 'feeling-lucky',
          filterText,
          activeFilter,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString()
        }));
      }
      
      // Redirect to login with flag indicating this was a planning attempt
      const returnUrl = action === 'planning' && formData?.tripType === 'ai-assist' ? '/ai-assist' : '/';
      navigate('/login', { 
        state: { 
          returnUrl: returnUrl, 
          isPlanning: true,
          message: 'Please sign in to start planning your trip'
        } 
      });
      return false;
    }
    return true;
  };

  // Helper function to check if form is valid based on trip type
  const isFormValid = () => {
    if (tripType === 'manual') {
      return selectedCountries.length > 0 && startDate && endDate;
    } else if (tripType === 'ai-assist') {
      return description && description.trim() && startDate && endDate;
    } else if (tripType === 'feeling-lucky') {
      return startDate && endDate;
    }
    return false;
  };

  // Render the component
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-100 to-white">
      {/* Hero Section */}
      <div className="w-full flex justify-center bg-transparent pb-4 md:h-[100vh] md:min-h-[800px] mt-2 md:mt-4">
        <div className="flex flex-col md:flex-row items-center justify-center w-full md:w-4/5 gap-6 md:gap-24 px-4 md:px-0 md:h-[70vh]">
          {/* Left Form Section */}
          <div className="flex bg-transparent rounded-xl w-full max-w-lg mx-auto md:h-[60vh] md:max-h-[70vh] items-center justify-center">
            <div className="w-full h-full flex flex-col justify-center py-6 md:py-0">
              <h1 className="text-black text-2xl md:text-4xl font-extrabold mb-4 mt-0 text-center w-full px-4" style={{fontFamily: 'Montserrat, sans-serif', color: 'black'}}>
                Design Your Dream Trip
              </h1>
              <form className="trip-form flex flex-col items-center w-full" onSubmit={handleSubmit}>
                {/* Trip Type Selection - Mobile reorganized, Desktop original */}
                {/* Mobile layout: stacked */}
<div className="flex md:hidden flex-col gap-2 my-3 w-full px-4">
                  {/* Top row: Manual and AI Assistant */}
                  <div className="flex gap-2 w-full">
                    <button
                      className={`flex-1 border rounded-lg cursor-pointer font-buenos-aires text-xs py-3 px-3 text-center transition-colors duration-300 whitespace-nowrap ${
                        tripType === 'manual' 
                          ? 'bg-blue-500 border-blue-500 text-white font-semibold' 
                          : 'bg-white border-gray-300 text-black hover:bg-gray-200 hover:text-black'
                      }`}
                      onClick={() => handleTripTypeChange('manual')}
                      type="button"
                    >
                      Manual
                    </button>
                    <button
                      className={`flex-1 border rounded-lg cursor-pointer font-buenos-aires text-xs py-3 px-3 text-center transition-colors duration-300 whitespace-nowrap ${
                        tripType === 'ai-assist' 
                          ? 'bg-blue-500 border-blue-500 text-white font-semibold' 
                          : 'bg-white border-gray-300 text-black hover:bg-gray-200 hover:text-black'
                      }`}
                      onClick={() => handleTripTypeChange('ai-assist')}
                      type="button"
                    >
                      AI Assistant
                    </button>
                  </div>
                  
                  {/* Bottom row: Feeling Lucky (wider) */}
                  <button
                    className={`w-full border rounded-lg cursor-pointer font-buenos-aires text-xs py-3 px-3 text-center transition-colors duration-300 whitespace-nowrap ${
                      tripType === 'feeling-lucky' 
                        ? 'bg-blue-500 border-blue-500 text-white font-semibold' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-200 hover:text-black'
                    }`}
                    onClick={() => handleTripTypeChange('feeling-lucky')}
                    type="button"
                  >
                    Feeling Lucky
                  </button>
                </div>

                {/* Desktop layout: original horizontal */}
                <div className="hidden md:flex justify-center gap-8 my-4 w-full">
                  <button
                    className={`w-[8.5rem] border rounded-lg cursor-pointer font-buenos-aires text-sm mb-[15px] py-4 px-3 text-center transition-colors duration-300 whitespace-nowrap ${
                      tripType === 'manual' 
                        ? 'bg-blue-500 border-blue-500 text-white font-semibold' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-200 hover:text-black'
                    }`}
                    onClick={() => handleTripTypeChange('manual')}
                    type="button"
                  >
                    Manual
                  </button>
                  <button
                    className={`w-[8.5rem] border rounded-lg cursor-pointer font-buenos-aires text-sm mb-[15px] py-3.5 px-3 text-center transition-colors duration-300 whitespace-nowrap ${
                      tripType === 'ai-assist' 
                        ? 'bg-blue-500 border-blue-500 text-white font-semibold' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-200 hover:text-black'
                    }`}
                    onClick={() => handleTripTypeChange('ai-assist')}
                    type="button"
                  >
                    AI Assistant
                  </button>
                  <button
                    className={`w-[8.5rem] border rounded-lg cursor-pointer font-buenos-aires text-sm mb-[15px] py-3.5 px-3 text-center transition-colors duration-300 whitespace-nowrap ${
                      tripType === 'feeling-lucky' 
                        ? 'bg-blue-500 border-blue-500 text-white font-semibold' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-200 hover:text-black'
                    }`}
                    onClick={() => handleTripTypeChange('feeling-lucky')}
                    type="button"
                  >
                    Feeling Lucky
                  </button>
                </div>

                {/* Tab content area - REMOVING debug bg and !important style */}
                <div className="relative flex-1 overflow-visible mb-2 w-full">
                {tripType === 'manual' && (
                    <div className="w-full box-border flex-shrink-0">
                      <h3 className="font-buenos-aires !font-semibold text-base mb-2 text-black">Search for a country</h3>
                      <CountrySearch 
                        onSelectCountry={handleAddCountry}
                        className="h-12"
                        placeholder="Select a country"
                        searchType="countries"
                      />
                      {selectedCountries.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedCountries.map(country => (
                            <div 
                              key={country.id} 
                              className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full flex items-center gap-1.5"
                            >
                              <span className="text-sm text-black">{country.name}</span>
                              <button 
                                type="button" 
                                onClick={() => handleRemoveCountry(country.id)}
                                className="text-black hover:text-gray-600 w-4 h-4 flex items-center justify-center"
                              >
                                <span>×</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                )}

                {tripType === 'ai-assist' && (
                    <div className="flex-shrink-0">
                      <div className="w-full box-border mb-4">
                        <h3 className="font-buenos-aires !font-semibold text-base mb-3 text-black">Describe Your Dream Trip</h3>
                        <div className="relative">
                          <CustomTextArea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            onFocus={() => setDescriptionFocused(true)}
                            onBlur={() => setDescriptionFocused(false)}
                            label="Describe your trip..."
                            minHeight="80px"
                          />
                        </div>
                      </div>
                      <div className="w-full box-border">
                        <h3 className="font-buenos-aires !font-semibold text-base mb-2.5 text-black">Include In Search</h3>
                        <div className="flex gap-2.5 flex-wrap">
                          <button
                            type="button"
                            className={`flex-1 py-2 px-3 rounded-lg border text-base transition-all box-border bg-white
                              ${searchOptions.flights 
                                ? 'border-blue-500 text-black font-semibold bg-blue-50' 
                                : 'border-gray-300 text-black hover:bg-gray-100'}`}
                            onClick={() => handleSearchOptionToggle('flights')}
                          >
                            Flights
                          </button>
                          <button
                            type="button"
                            className={`flex-1 py-2 px-3 rounded-lg border text-base transition-all box-border bg-white
                              ${searchOptions.accommodation 
                                ? 'border-blue-500 text-black font-semibold bg-blue-50' 
                                : 'border-gray-300 text-black hover:bg-gray-100'}`}
                            onClick={() => handleSearchOptionToggle('accommodation')}
                          >
                            Accommodation
                          </button>
                          <button
                            type="button"
                            className={`flex-1 py-2 px-3 rounded-lg border text-base transition-all box-border bg-white
                              ${searchOptions.transport 
                                ? 'border-blue-500 text-black font-semibold bg-blue-50' 
                                : 'border-gray-300 text-black hover:bg-gray-100'}`}
                            onClick={() => handleSearchOptionToggle('transport')}
                          >
                            Transport
                          </button>
                        </div>
                      </div>
                    </div>
                )}

                {tripType === 'feeling-lucky' && (
                    <div className="bg-[#f9fafc] p-[18px] rounded-xl box-border overflow-visible flex-shrink-0">
                    <div className="mt-1.5 w-full">
                      <h3 className="font-buenos-aires !font-semibold text-base mb-2.5 flex align-items-start text-black">
                        Narrow My Surprise <span className="text-black opacity-60 font-normal ml-1"> (Optional)</span>
                      </h3>
                      <div className="flex flex-wrap justify-center gap-2">
                        <button
                          className={`flex-1 py-2.5 px-0 rounded-lg border text-sm transition-colors box-border
                            ${activeFilter === 'region' 
                              ? 'border-blue-500 text-black font-semibold bg-blue-50' 
                              : 'border-gray-300 bg-white text-black hover:bg-gray-100'}`}
                          onClick={() => handleFilterClick('region')}
                          type="button"
                        >
                          Any Region
                        </button>
                        <button
                          className={`flex-1 py-2.5 px-0 rounded-lg border text-sm transition-colors box-border
                            ${activeFilter === 'climate' 
                              ? 'border-blue-500 text-black font-semibold bg-blue-50' 
                              : 'border-gray-300 bg-white text-black hover:bg-gray-100'}`}
                          onClick={() => handleFilterClick('climate')}
                          type="button"
                        >
                          Any Climate
                        </button>
                        <button
                          className={`flex-1 py-2.5 px-0 rounded-lg border text-sm transition-colors box-border
                            ${activeFilter === 'budget' 
                              ? 'border-blue-500 text-black font-semibold bg-blue-50' 
                              : 'border-gray-300 bg-white text-black hover:bg-gray-100'}`}
                          onClick={() => handleFilterClick('budget')}
                          type="button"
                        >
                          Any Budget
                        </button>
                        <button
                          className={`flex-1 py-2.5 px-0 rounded-lg border text-sm transition-colors box-border
                            ${activeFilter === 'other' 
                              ? 'border-blue-500 text-black font-semibold bg-blue-50' 
                              : 'border-gray-300 bg-white text-black hover:bg-gray-100'}`}
                          onClick={() => handleFilterClick('other')}
                          type="button"
                        >
                          Other
                        </button>
                      </div>
                      
                      {showTextBox && activeFilter && (
                        <div className="mt-4">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder=" "
                              value={filterText}
                              onChange={(e) => setFilterText(e.target.value)}
                              className={`w-4/5 max-w-[500px] rounded-lg border border-gray-300 text-base box-border h-12 px-4 py-3 font-buenos-aires transition-all duration-200 ${filterText ? 'pt-5 pb-1' : ''}`}
                            />
                            <span className={`absolute left-4 pointer-events-none transition-all duration-200 font-buenos-aires ${filterText ? 'top-1.5 text-xs font-medium text-black bg-white px-1 -ml-1' : 'top-1/2 -translate-y-1/2 text-base text-black opacity-60'}`}>{`Enter ${activeFilter} preference...`}</span>
                          </div>
                          <p className="text-sm text-black opacity-60 mt-1">
                            {activeFilter === 'region' && 'e.g., "Southeast Asia", "Mediterranean", "Scandinavia"'}
                            {activeFilter === 'climate' && 'e.g., "tropical", "mediterranean", "alpine"'}
                            {activeFilter === 'budget' && 'e.g., "budget", "mid-range", "luxury"'}
                            {activeFilter === 'other' && 'e.g., "beach", "historical", "adventure"'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>

                {/* Date Selection Section */}
                <div className="w-full box-border mb-4 flex-shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-buenos-aires !font-semibold text-base text-black">Select dates</h3>
                  </div>
                  {isMobile ? (
                    <MobileCalendar
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateChange}
                      minDate={new Date()}
                      maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 2))}
                      filterDate={date => !isPastDate(date)}
                    />
                  ) : (
                    <div className="flex justify-between w-full gap-4 box-border">
                      <div className="relative flex-1 box-border">
                        <CustomDateInput
                          value={startDate ? startDate.toLocaleDateString() : ''}
                          onClick={handleStartDateClick}
                          label="Start date"
                        />
                      {isOpen && activeInput === 'start' && (
                        <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg z-50 w-[640px] transform -translate-x-1/4" ref={calendarRef}>
                          <div className="flex justify-center gap-1 my-1 px-2 relative z-10">
                            <button 
                              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${!isFlexible ? 'bg-blue-500 text-white' : 'bg-white border border-gray-300 text-black hover:bg-gray-50'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFlexible(false);
                              }}
                            >
                              Specific dates
                            </button>
                            <button 
                              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isFlexible ? 'bg-blue-500 text-white' : 'bg-white border border-gray-300 text-black hover:bg-gray-50'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFlexible(true);
                              }}
                            >
                              Flexible dates
                            </button>
                          </div>
                          {!isFlexible ? (
                            <>
                              {isMobile ? (
                                <MobileCalendar
                                  startDate={startDate}
                                  endDate={endDate}
                                  onChange={handleDateChange}
                                  minDate={new Date()}
                                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 2))}
                                  filterDate={date => !isPastDate(date)}
                                />
                              ) : (
                                <div>
                                  <DatePicker
                                    selectsRange={true}
                                    startDate={startDate || undefined}
                                    endDate={endDate || undefined}
                                    onChange={handleDateChange}
                                    monthsShown={2}
                                    inline
                                    calendarClassName="border-none shadow-none rounded-xl p-2"
                                    minDate={new Date()}
                                    maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 2))}
                                    filterDate={date => !isPastDate(date)}
                                    showPopperArrow={false}
                                  />
                                </div>
                              )}
                              <div className="flex justify-center items-center w-full py-2 border-t border-gray-100 mt-2 mb-2">
                                <div className="grid grid-cols-5 gap-3 w-full px-4">
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm bg-blue-500 text-white font-medium shadow-sm"
                                    onClick={() => handleQuickSelect(null)}
                                  >
                                    Exact dates
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm border border-gray-300 text-black hover:bg-gray-50 shadow-sm"
                                    onClick={() => handleQuickSelect(1)}
                                  >
                                    ±1 day
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm border border-gray-300 text-black hover:bg-gray-50 shadow-sm"
                                    onClick={() => handleQuickSelect(2)}
                                  >
                                    ±2 days
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm border border-gray-300 text-black hover:bg-gray-50 shadow-sm"
                                    onClick={() => handleQuickSelect(3)}
                                  >
                                    ±3 days
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm border border-gray-300 text-black hover:bg-gray-50 shadow-sm"
                                    onClick={() => handleQuickSelect(7)}
                                  >
                                    ±7 days
                                  </button>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="p-2" style={{display: 'flex', flexDirection: 'row', width: '100%'}}>
                              <div className="grid grid-cols-3 gap-4 w-full" style={{flex: 1, minWidth: '580px'}}>
                                {getNext12Months().map((monthData, index) => (
                                  <button
                                    key={`${monthData.month}-${monthData.year}`}
                                    type="button"
                                    className={`p-4 text-center border rounded-lg transition-colors min-h-[80px] flex flex-col justify-center w-full ${
                                      selectedMonths.includes(`${monthData.month} ${monthData.year}`)
                                        ? 'bg-blue-500 text-white border-blue-500'
                                        : 'bg-white border-gray-300 text-black hover:bg-gray-50'
                                    }`}
                                    onClick={() => handleMonthClick(monthData.month, monthData.year)}
                                  >
                                    <div className="text-sm text-black opacity-60">{monthData.year}</div>
                                    <div className="font-medium">{monthData.month}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="relative flex-1 box-border">
                      <CustomDateInput
                        value={endDate ? endDate.toLocaleDateString() : ''}
                        onClick={handleEndDateClick}
                        label="End date"
                      />
                      {isOpen && activeInput === 'end' && (
                        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg z-50 w-[640px] transform translate-x-1/4" ref={calendarRef}>
                          <div className="flex justify-center gap-1 my-1 px-2 relative z-10">
                            <button 
                              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${!isFlexible ? 'bg-blue-500 text-white' : 'bg-white border border-gray-300 text-black hover:bg-gray-50'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFlexible(false);
                              }}
                            >
                              Specific dates
                            </button>
                            <button 
                              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isFlexible ? 'bg-blue-500 text-white' : 'bg-white border border-gray-300 text-black hover:bg-gray-50'}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFlexible(true);
                              }}
                            >
                              Flexible dates
                            </button>
                          </div>
                          {!isFlexible ? (
                            <>
                              {isMobile ? (
                                <MobileCalendar
                                  startDate={startDate}
                                  endDate={endDate}
                                  onChange={handleDateChange}
                                  minDate={new Date()}
                                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 2))}
                                  filterDate={date => !isPastDate(date)}
                                />
                              ) : (
                                <div>
                                  <DatePicker
                                    selectsRange={true}
                                    startDate={startDate || undefined}
                                    endDate={endDate || undefined}
                                    onChange={handleDateChange}
                                    monthsShown={2}
                                    inline
                                    calendarClassName="border-none shadow-none rounded-xl p-2"
                                    minDate={new Date()}
                                    maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 2))}
                                    filterDate={date => !isPastDate(date)}
                                    showPopperArrow={false}
                                  />
                                </div>
                              )}
                              <div className="flex justify-center items-center w-full py-2 border-t border-gray-100 mt-2 mb-2">
                                <div className="grid grid-cols-5 gap-3 w-full px-4">
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm bg-blue-500 text-white font-medium shadow-sm"
                                    onClick={() => handleQuickSelect(null)}
                                  >
                                    Exact dates
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm border border-gray-300 text-black hover:bg-gray-50 shadow-sm"
                                    onClick={() => handleQuickSelect(1)}
                                  >
                                    ±1 day
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm border border-gray-300 text-black hover:bg-gray-50 shadow-sm"
                                    onClick={() => handleQuickSelect(2)}
                                  >
                                    ±2 days
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm border border-gray-300 text-black hover:bg-gray-50 shadow-sm"
                                    onClick={() => handleQuickSelect(3)}
                                  >
                                    ±3 days
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-2 rounded-full text-sm border border-gray-300 text-black hover:bg-gray-50 shadow-sm"
                                    onClick={() => handleQuickSelect(7)}
                                  >
                                    ±7 days
                                  </button>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="p-2" style={{display: 'flex', flexDirection: 'row', width: '100%'}}>
                              <div className="grid grid-cols-3 gap-4 w-full" style={{flex: 1, minWidth: '580px'}}>
                                {getNext12Months().map((monthData, index) => (
                                  <button
                                    key={`${monthData.month}-${monthData.year}`}
                                    type="button"
                                    className={`p-4 text-center border rounded-lg transition-colors min-h-[80px] flex flex-col justify-center w-full ${
                                      selectedMonths.includes(`${monthData.month} ${monthData.year}`)
                                        ? 'bg-blue-500 text-white border-blue-500'
                                        : 'bg-white border-gray-300 text-black hover:bg-gray-50'
                                    }`}
                                    onClick={() => handleMonthClick(monthData.month, monthData.year)}
                                  >
                                    <div className="text-sm text-black opacity-60">{monthData.year}</div>
                                    <div className="font-medium">{monthData.month}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                {tripType === 'feeling-lucky' ? (
                  <button 
                    className="w-full py-4 bg-blue-500 hover:bg-blue-700 text-white text-base rounded-lg font-bold transition-colors box-border flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    onClick={handleSurpriseMe}
                    disabled={isGeneratingSurprise || !isFormValid()}
                  >
                    {isGeneratingSurprise ? '🔄 Generating your surprise...' : 
                     !isFormValid() ? 'Please select dates' : 
                     '✨ Show me a surprise destination!'}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="w-full bg-blue-500 text-white py-3 px-4 hover:bg-blue-600 transition-colors duration-300 font-semibold text-button rounded-button"
                  >
                    Start Planning
                  </button>
                )}
              </form>
            </div>
          </div>

          {/* Right Map Section - Hidden on mobile */}
          <div className="hidden md:flex w-5/12 h-full items-center justify-center">
            <div className="h-full w-full border border-gray-300 relative rounded-xl overflow-hidden shadow-lg bg-white/80">
              {isGoogleMapsLoaded ? (
                <MapComponent
                  locations={selectedCountries.map(country => country.name) || []}
                  cityColors={cityColors}
                />
              ) : (
                <div className="h-full w-full flex flex-col items-center justify-center bg-gray-50 p-6">
                  {!mapLoadTimeout ? (
                    <>
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                      <p className="text-gray-600 text-sm">Loading map...</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {window.google?.maps ? 'Initializing...' : 'Connecting to maps service...'}
                      </p>
                    </>
                  ) : (
                    <div className="text-center">
                      <div className="text-gray-400 text-4xl mb-4">🗺️</div>
                      <p className="text-gray-600 text-sm mb-2">Map temporarily unavailable</p>
                      <p className="text-gray-400 text-xs">
                        You can still plan your trip below
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Additional sections/components on the homepage */}
      {/* Hide CountryMap (Explore Our Destinations) on mobile */}
      <div className="hidden md:block">
        <CountryMap />
      </div>
      <hr className="hidden md:block border-none h-px bg-gray-200 my-10 mx-auto w-4/5" />
      <KindOfTrip
        tripType={tripType}
        onTripTypeChange={handleTripTypeChange}
        options={[
          { value: 'manual' as TripType, label: 'Manual' },
          { value: 'ai-assist' as TripType, label: 'AI Assist' },
          { value: 'feeling-lucky' as TripType, label: 'Feeling Lucky' },
        ]}
      />
      <WhyUs />
      <LoginSection />
    </div>
  );
};

export default Homepage;